import { computed } from 'vue';
import { cloneDeep } from 'lodash';
import { useI18n } from '@/composables';

export interface TabMeta {
  icon?: string;
  title?: string;
}

export interface TabMetaBase extends TabMeta {
  inheritIcon?: string;
}

export const useMainMenuComplexTabsMeta = () => {
  const { t } = useI18n();

  const customKeys = computed(() =>
    Array.apply(null, { length: 10 } as any).map(
      (value, index) => `custom${index + 1}Name`,
    ),
  );

  const customtabsMetaBase = computed((): { [type: string]: TabMetaBase } => {
    const result: { [type: string]: TabMetaBase } = {
      custom: {
        icon: 'fas fa-cogs',
      },
    };

    customKeys.value.forEach((item, index) => {
      const key = `custom${index + 1}`;
      result[key] = {
        inheritIcon: 'custom',
      };
    });

    return result;
  });

  const tabsMetaBase = computed((): { [type: string]: TabMetaBase } => ({
    countries: {
      icon: 'fa fa-globe-europe',
      title: t('Countries'),
    },
    languages: {
      inheritIcon: 'countries',
      title: t('Languages'),
    },
    cities: {
      inheritIcon: 'countries',
      title: t('Cities'),
    },
    states: {
      inheritIcon: 'countries',
      title: t('State / Region'),
    },
    isps: {
      icon: 'fa fa-wifi',
      title: t('ISP'),
    },
    'mobile-carriers': {
      inheritIcon: 'isps',
      title: t('Mobile Carriers'),
    },
    'connection-types': {
      inheritIcon: 'isps',
      title: t('Connection Types'),
    },
    ips: {
      icon: 'fas fa-exchange-alt',
      title: t('IPs'),
    },
    'subnet-small': {
      inheritIcon: 'ips',
      title: t('Subnets (Small)'),
    },
    'subnet-big': {
      inheritIcon: 'ips',
      title: t('Subnets (Big)'),
    },
    devices: {
      icon: 'fa fa-laptop',
      title: t('Devices'),
    },
    'device-models': {
      inheritIcon: 'devices',
      title: 'Device Models',
    },
    'device-vendors': {
      inheritIcon: 'devices',
      title: t('Device Vendors'),
    },
    'device-types': {
      inheritIcon: 'devices',
      title: t('Device Types'),
    },
    'screen-resolutions': {
      inheritIcon: 'devices',
      title: t('Screen Resolutions'),
    },
    oses: {
      icon: 'fa fa-mobile',
      title: t('OS'),
    },
    'os-versions': {
      inheritIcon: 'oses',
      title: t('OS Versions'),
    },
    browsers: {
      icon: 'fas fa-browser',
      title: t('Browsers'),
    },
    'browser-versions': {
      inheritIcon: 'browsers',
      title: t('Browser Versions'),
    },
    useragents: {
      inheritIcon: 'browsers',
      title: t('User Agents'),
    },
    referrers: {
      icon: 'fas fa-compass',
      title: t('Referrers'),
    },
    'referrer-domains': {
      inheritIcon: 'referrers',
      title: t('Referrer Domains'),
    },
    lives: {
      icon: 'fas fa-wave-square',
      title: t('Live Visits'),
    },
    period: {
      icon: 'far fa-clock',
      title: t('Period'),
    },
    month: {
      inheritIcon: 'period',
      title: t('Month'),
    },
    day: {
      inheritIcon: 'period',
      title: t('Day of Month'),
    },
    'day-of-week': {
      inheritIcon: 'period',
      title: t('Day Of Week'),
    },
    'hour-of-day': {
      inheritIcon: 'period',
      title: t('Hour Of Day'),
    },
    campaigns: {
      icon: 'fa fa-bullseye-arrow',
      title: t('Campaigns'),
    },
    offers: {
      icon: 'fa fa-badge-dollar',
      title: t('Offers'),
    },
    landings: {
      icon: 'fa fa-browser',
      title: t('Landings'),
    },
    flows: {
      icon: 'fas fa-sitemap',
      title: t('Flows'),
    },
    'traffic-sources': {
      icon: 'fa fa-globe',
      title: t('Traffic Sources'),
    },
    'affiliate-networks': {
      icon: 'fa fa-users-class',
      title: t('Affiliate Networks'),
    },
    conversions: {
      icon: 'fas fa-dollar-sign',
      title: t('Conversions'),
    },
    postbacks: {
      icon: 'fa fa-download',
      title: t('Postbacks'),
    },
    errors: {
      icon: 'fa fa-exclamation-circle',
      title: t('Errors'),
    },
    flow_rule_paths: {
      title: t('Flow path'),
      icon: 'fa fa-exclamation-circle',
    },
    flow_rules: {
      title: t('Flow rule'),
      icon: 'fa fa-exclamation-circle',
    },
    ...customtabsMetaBase.value,
  }));

  const tabsMeta = computed((): { [type: string]: TabMeta } => {
    const tabsMetaBaseValue = cloneDeep(tabsMetaBase.value);
    const keys = Object.keys(tabsMetaBaseValue);
    const result: { [type: string]: TabMetaBase } = {};

    keys.forEach((key) => {
      const item = tabsMetaBaseValue[key];
      if (item.inheritIcon) {
        item.icon = tabsMetaBaseValue[item.inheritIcon].icon;
        delete item.inheritIcon;
      }

      result[key] = item;
    });

    return result;
  });

  return {
    customKeys,
    tabsMeta,
  };
};
